import colors from '@/constants/colors';
import { useResponsive } from '@/hooks/useResponsive';
import { useOS } from '@/hooks/useOS';
import { withTranslation } from '@/i18n';
import styles from '@/interface/ui/t/[id].module.scss';
import FloatingActionMenu from '@/interface/ui/t/_component/FloatingActionMenu';
import Layout from '@/interface/ui/_shared/layout/index';
import type { RootState } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import E404 from '@/interface/ui/404/index';

// types
import type { NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useMemo, useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Carousel } from 'react-responsive-carousel';
import { Modal } from '../_shared/modal';
import { storeMarket } from '@/constants/app';
import { formatPriceToYen } from '@/util/currencyHelper';
import CalenderButtonGroup from '@/interface/ui/_shared/items/calenderButtonGroup';
import { WithTranslation } from 'next-i18next';

interface Props extends WithTranslation {
  statusCode?: number;
  mode?: 'default' | 'pink';
}

const mainStyle = { backgroundColor: colors.white };

const renderCarouselIndicator: (
  clickHandler: (e: React.MouseEvent | React.KeyboardEvent) => void,
  isSelected: boolean,
  index: number,
  label: string
) => React.ReactNode = (clickHandler, isSelected, index, label) => {
  const colorClass = isSelected
    ? styles.carouselSelectedIndicator
    : styles.carouselNotSelectedIndicator;
  return (
    <li
      className={`${styles.carouselIndicatorBase} ${colorClass}`}
      onClick={clickHandler}
      value={index}
      key={index}
      role="button"
      tabIndex={0}
      aria-label={`${label} ${index + 1}`}
    ></li>
  );
};

const Page: NextPage<Props> = ({ statusCode, mode }) => {
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);

  const { isDesktop } = useResponsive();
  const { osName } = useOS();

  const itemId = router.query.id as string;
  const item = useSelector(
    (state: RootState) => state.domain.itemDetails.byId[itemId]
  );

  const releasedAt = useMemo(
    () =>
      !item?.releaseAt ? '--' : dayjs(item.releaseAt).format('YYYY.MM.DD'),
    [item?.releaseAt]
  );
  const calendarWrapperRef = useRef<{
    togglePopOver: () => void;
  }>();
  const { userAgent } = useSelector((state: RootState) => state.app);

  const toggleCalender = () => {
    calendarWrapperRef && calendarWrapperRef.current?.togglePopOver();
  };

  const renderCarousel = () => {
    if (!item) return null;

    return (
      <Carousel
        className={styles.carousel}
        autoPlay
        infiniteLoop
        renderIndicator={renderCarouselIndicator}
        showIndicators={item.images.length > 1}
        showStatus={false}
        showThumbs={isDesktop && item.images.length > 1}
      >
        {item.images.map((image, index) => (
          <div className={styles.carouselItem} key={index}>
            <img src={item.originImages[index]?.url || image.url} />
          </div>
        ))}
      </Carousel>
    );
  };

  const renderProductModel = () => {
    if (!item) return null;

    const productTitle = item.title;
    return (
      <div className={styles.topInfoContainer}>
        {/* <p className={styles.brandName}>{item.brandName}</p> */}
        <h1 className={styles.title}>{productTitle}</h1>
        <p className={styles.modelNumber}>{item.modelNumber}</p>
      </div>
    );
  };

  const renderProductPrice = () => {
    if (!item) return null;

    return (
      <table className={styles.infoTable}>
        <tbody>
          {!!item.lotteryTerm && !item.launched && (
            <tr className={styles.infoTableRow}>
              <td className={styles.infoTableLabel}>抽選期間</td>
              <td className={styles.infoTableValue}>{item.lotteryTerm}</td>
            </tr>
          )}

          <tr className={styles.infoTableRow}>
            <td className={styles.infoTableLabel}>発売日</td>
            <td className={styles.infoTableValue}>
              {item.releaseAtDisplay || releasedAt}
            </td>
          </tr>
          <tr className={styles.infoTableRow}>
            <td className={styles.infoTableLabel}>定価</td>
            <td className={styles.infoTableValue}>
              {formatPriceToYen(item.price)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderProductAction = () => {
    if (!item) return null;

    return (
      <div className={styles.actionButtonContainer}>
        <button
          className={styles.actionButton}
          onClick={() => setShowModal(true)}
        >
          <span className={styles.iconWrapper}>
            <FontAwesomeIcon
              icon="star"
              size="lg"
              color={colors.lightGray}
              className={styles.icon}
            />
            <span className={styles.actionButtonLabelText}>WANT</span>
          </span>
          <span className={styles.actionButtonCount}>{item.likeCount}</span>
        </button>

        {item.launched && (
          <button
            className={styles.actionButton}
            onClick={() => setShowModal(true)}
          >
            <span className={styles.iconWrapper}>
              <FontAwesomeIcon
                icon="check-circle"
                size="lg"
                color={colors.lightGray}
                className={styles.icon}
              />
              <span className={styles.actionButtonLabelText}>HAVE</span>
            </span>
            <span className={styles.actionButtonCount}>{item.haveCount}</span>
          </button>
        )}
        {!item.launched && !item.releaseAtDisplay && (
          <button className={styles.actionButton} onClick={toggleCalender}>
            <span className={styles.iconWrapper}>
              <CalenderButtonGroup
                item={item}
                userAgent={userAgent}
                buttonStyle={styles.icon}
                ref={calendarWrapperRef}
              />
              <span className={styles.actionCalendarButtonLabelText}>
                カレンダーに登録
              </span>
            </span>
          </button>
        )}
      </div>
    );
  };

  const renderFanThumb = () => {
    return null;
    // if (item.iineUsers.length) {
    //   return (
    //     <ul className={styles.iineUsersContainer}>
    //       {item.iineUsers.map((user, index) => {
    //         return (
    //           <li key={index} className={styles.funThumbnailCircleButton}>
    //             <img src="" alt="" />
    //           </li>
    //         );
    //       })}
    //     </ul>
    //   );
    // }
  };

  const renderModal = useCallback(() => {
    return (
      <Modal
        title={<p />}
        onPress={() => setShowModal(false)}
        isStoreLink
        osType={osName}
      >
        <></>
      </Modal>
    );
  }, [osName]);

  const renderBluring = useCallback(() => {
    if (!item) return null;

    const osLink =
      osName === 'Mac OS' || osName === 'iOS'
        ? storeMarket.appleStoreUrl
        : storeMarket.googlePlayUrl;

    return (
      <div className={styles.bluringWrapper}>
        <img
          src="/static/images/mosaic_bg.png"
          alt="japanFlag"
          className={styles.blurImage}
        />
        <div className={styles.buttonContainer}>
          <p className={styles.title}>atmosアプリでこちらの機能が使えます</p>
          <a
            href={osLink}
            target="_blank"
            rel="noreferrer"
            className={styles.buttonWrapper}
          >
            <p className={styles.buttonTitle}>アプリダウンロード</p>
          </a>
        </div>
      </div>
    );
  }, [item, osName]);

  const renderBtn = () => {
    if (!item.shopUrl) return null;

    const urlWithUtmPrams = googleParamsGenerator(item.shopUrl);

    return (
      <div>
        <button
          className={styles.buySneakerButton}
          onClick={() => window.open(urlWithUtmPrams, '_blank')}
        >
          {item.buttonLabel}
        </button>
      </div>
    );
  };

  if (statusCode === 404) {
    return <E404 />;
  }

  return (
    <>
      <Head>
        <title>{item.title}</title>
        <meta name="description" content={item.description} />
        <meta property="og:title" content={item.title} key="title" />
      </Head>
      <Layout style={mainStyle} mode={mode}>
        <div className={styles.container}>
          <div className={styles.productWrapper}>
            {renderCarousel()}
            <div>
              {renderProductModel()}
              {renderProductPrice()}
              {renderProductAction()}
              {renderFanThumb()}
              <div className={styles.descriptionContainer}>
                <p>{item.description}</p>
              </div>
              {!!item.html_description && (
                <div className={styles.descriptionContainer}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: item.html_description,
                    }}
                  />
                </div>
              )}
              {renderBtn()}

              {isDesktop && renderBluring()}
            </div>
          </div>

          {!isDesktop && renderBluring()}
          {/* フローティングメニュー */}
          <FloatingActionMenu
            onClickLeft={() => console.log('left')}
            onClickLeftTop={() => console.log('left top')}
            onClickTop={() => console.log('top')}
          />
        </div>
        {showModal && renderModal()}
      </Layout>
    </>
  );
};

// googleアナリティクス用のパラメータを付与
const googleParamsGenerator = (uri: string) => {
  const connecter = uri.includes('?') ? '&' : '?';
  const itemId = extractItemId(uri) ?? '';

  return (
    uri +
    connecter +
    'utm_medium=referral&utm_source=atmos_lists&utm_campaign=product&utm_content=' +
    itemId
  );
};

// urlから商品idを抽出
const extractItemId = (uri?: string): string | undefined => {
  if (!uri) {
    return undefined;
  }

  const uriWithoutSearch = uri.split('?')[0];
  const routes = uriWithoutSearch.split('/');
  if (!routes.length) {
    return undefined;
  }

  const last = routes[routes.length - 1];
  return encodeURI(last);
};

export default withTranslation('common')(Page);
